import { useState } from "react";
import { App } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "@/store";
import { User, UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import CustomTabPane, { TabProps } from "@/components/TabPane";
import { Equipment, EquipmentCategoryParams } from "@/types/equipment";
import CategoryDetail from "../../components/CategoryDetail";
import CategoryForm, {
  CategoryFormMode,
} from "../../components/CategoryForm";
import CategoryTree from "../../components/CategoryTree";
import EquipmentList from "../../components/EquipmentList";
import EquipmentRecords from "../../components/EquipmentRecords";
import useEquipmentCategories from "../../hooks/useEquipmentCategories";
import {
  CategoryContentPanel,
  EquipmentWorkspace,
  EquipmentWrap,
} from "../../style";

// 台账内的固定列表与动态记录流水使用稳定 key 管理。
const WORKSPACE_LIST_KEY = "equipment-list";
const WORKSPACE_RECORDS_KEY = "equipment-records";

// 设备台账负责协调类目树、类目操作及设备工作区页签。
const EquipmentLedger = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const currentUser: User = useSelector(
    (state: RootState) => state.userReducer.curUser,
  );
  const canEditCategory = hasPermission(currentUser, [
    UserPermissionEnum.EQUIPMENT_CATEGORY_ADD_OR_EDIT,
  ]);
  // 表单模式统一控制根类目、新增子类目和编辑类目三种场景。
  const [formMode, setFormMode] = useState<CategoryFormMode>();
  const {
    visibleTree,
    selectedId,
    selectedCategory,
    expandedKeys,
    treeLoading,
    detailLoading,
    actionLoading,
    selectCategory,
    createRoot,
    createChild,
    editCategory,
    setCategoryEnabled,
    setExpandedKeys,
  } = useEquipmentCategories();

  const handleSelectCategory = (id: string) => {
    void selectCategory(id);
  };

  const handleFormSubmit = async (body: EquipmentCategoryParams) => {
    if (!canEditCategory) return;

    let savedCategory;
    if (formMode === "root") {
      savedCategory = await createRoot(body);
    } else if (formMode === "child" && selectedCategory) {
      savedCategory = await createChild(selectedCategory.id, body);
    } else if (formMode === "edit" && selectedCategory) {
      savedCategory = await editCategory(selectedCategory.id, body);
    }

    if (!savedCategory) {
      return;
    }

    notification.success({
      message: t("equipment.categoryOperationSucceeded"),
    });
    setFormMode(undefined);
  };

  // 提交当前类目的单字段编辑，并将成功状态反馈给内联编辑组件。
  const handleCategoryEdit = async (body: EquipmentCategoryParams) => {
    if (!canEditCategory || !selectedCategory) return false;

    const savedCategory = await editCategory(selectedCategory.id, body);
    if (!savedCategory) return false;

    notification.success({
      message: t("equipment.categoryOperationSucceeded"),
    });
    return true;
  };

  const handleSetCategoryEnabled = async (enabled: boolean) => {
    if (!canEditCategory || !selectedCategory) return;

    const succeeded = await setCategoryEnabled(selectedCategory.id, enabled);
    if (!succeeded) {
      return;
    }

    notification.success({
      message: t("equipment.categoryOperationSucceeded"),
    });
  };

  // 使用同一个动态页签承载当前设备的记录流水，避免重复打开。
  const recordTabs = (equipment: Equipment): Record<string, TabProps> => ({
    records: {
      key: WORKSPACE_RECORDS_KEY,
      label: `${equipment.number || equipment.name} ${t("equipment.recordFlow")}`,
      children: <EquipmentRecords equipmentId={equipment.id} />,
    },
  });

  // 选中任意层级类目后均挂载设备列表与动态流水页签。
  return (
    <EquipmentWrap>
      <CategoryTree
        treeData={visibleTree}
        selectedId={selectedId}
        expandedKeys={expandedKeys}
        loading={treeLoading}
        onSelect={handleSelectCategory}
        onExpand={setExpandedKeys}
        onAddRoot={() => setFormMode("root")}
        canEdit={canEditCategory}
      />
      <CategoryContentPanel>
        {/* 切换类目时重建详情组件，清理上一类目的内联编辑状态。 */}
        <CategoryDetail
          key={selectedCategory?.id}
          category={selectedCategory}
          loading={detailLoading}
          actionLoading={actionLoading}
          onAddChild={() => setFormMode("child")}
          onEdit={handleCategoryEdit}
          onSetEnabled={handleSetCategoryEnabled}
          canEdit={canEditCategory}
        />
        {selectedCategory && !detailLoading && (
          <EquipmentWorkspace>
            <CustomTabPane<Equipment>
              key={selectedCategory.id}
              defaultKey={WORKSPACE_LIST_KEY}
              defaultTabs={[
                {
                  key: WORKSPACE_LIST_KEY,
                  label: t("equipment.equipmentList"),
                  children: <EquipmentList category={selectedCategory} />,
                  closable: false,
                },
              ]}
              onOpens={recordTabs}
              style={{ height: "100%" }}
            />
          </EquipmentWorkspace>
        )}
      </CategoryContentPanel>
      <CategoryForm
        open={canEditCategory && Boolean(formMode)}
        mode={formMode ?? "root"}
        category={formMode === "child" || formMode === "edit"
          ? selectedCategory
          : undefined}
        confirmLoading={actionLoading}
        onCancel={() => setFormMode(undefined)}
        onSubmit={handleFormSubmit}
      />
    </EquipmentWrap>
  );
};

export default EquipmentLedger;
